// ============================================================
// 设备检测 Hook（纯逻辑，无 JSX）
// ============================================================

import { useState, useEffect, useMemo } from 'react'

// ─── 断点 ───────────────────────────────────────────────────

const BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
} as const

export type DeviceType = 'mobile' | 'tablet' | 'desktop'

// ─── useMediaQuery ──────────────────────────────────────────

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia(query).matches
  })

  useEffect(() => {
    const mql = window.matchMedia(query)
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches)
    mql.addEventListener('change', handler)
    setMatches(mql.matches)
    return () => mql.removeEventListener('change', handler)
  }, [query])

  return matches
}

// ─── useDevice ──────────────────────────────────────────────

export interface DeviceInfo {
  /** 设备类型 */
  device: DeviceType
  /** 是否桌面端（>=1024） */
  isDesktop: boolean
  /** 是否平板（768-1023） */
  isTablet: boolean
  /** 是否移动端（<768） */
  isMobile: boolean
  /** 屏幕宽度 */
  screenWidth: number
}

export function useDevice(): DeviceInfo {
  const isMobile = useMediaQuery(`(max-width: ${BREAKPOINTS.mobile - 1}px)`)
  const isTabletQuery = useMediaQuery(
    `(min-width: ${BREAKPOINTS.mobile}px) and (max-width: ${BREAKPOINTS.tablet - 1}px)`,
  )
  const [screenWidth, setScreenWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1440,
  )

  useEffect(() => {
    const handler = () => setScreenWidth(window.innerWidth)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  const device: DeviceType = isMobile ? 'mobile' : isTabletQuery ? 'tablet' : 'desktop'

  return useMemo(
    () => ({
      device,
      isDesktop: device === 'desktop',
      isTablet: device === 'tablet',
      isMobile: device === 'mobile',
      screenWidth,
    }),
    [device, screenWidth],
  )
}
